import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InputField from '../../Components/InputField';
import InlineErrorMessage from '../../Components/InlineErrorMessage';
import AuthLayout from '../../Components/AuthLayout';
import { authenticateWithEmail } from '../../Services/auth';
import { getProducts } from '../../Services/subscription';
import { findPlanProduct } from '../../Utils/planUtils';
import './style.css';

const EmailInputPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [planInfo, setPlanInfo] = useState(null);

  const planName = searchParams.get('plan');
  const subscriptionMode = searchParams.get('subscription_mode');

  useEffect(() => {
    if (planName && subscriptionMode) {
      fetchPlanInfo();
    }
  }, [planName, subscriptionMode]);

  const fetchPlanInfo = async () => {
    try {
      const response = await getProducts();
      if (response?.payload?.products) {
        const planData = findPlanProduct(response.payload.products, planName, subscriptionMode);

        if (planData) {
          setPlanInfo({
            name: planData.displayName,
            price: planData.price,
            interval: planData.interval
          });
        }
      }
    } catch (err) {
      console.error('Error fetching plan info:', err);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email) || isSending) return;

    setIsSending(true);
    setError('');

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = {
        email: email.toLowerCase(),
        timezone: timezone
      };

      const response = await authenticateWithEmail(payload.email, payload.timezone);

      if (response.status === 200 && response.payload) {
        // Navigate to OTP page with state
        const otpState = {
          email: email,
          sessionInfo: response.payload.sessionInfo,
          isNewUser: response.payload.isNewUser || false,
          isVerified: response.payload.isVerified,
          authMethod: 'email'
        };

        // Preserve query params
        const queryString = searchParams.toString();
        navigate(`/login/verify-otp?${queryString}`, { state: otpState });
      } else {
        const errorMsg = response.message || 'Failed to send OTP. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to send OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    const queryString = searchParams.toString();
    navigate(`/login${queryString ? '?' + queryString : ''}`);
  };

  return (
    <AuthLayout showBackButton={false} onBack={handleBack}>
      {planInfo && (
        <p className="auth-plan-info">
          You're getting: {planInfo.name} - ${planInfo.price}/{planInfo.interval === 'yearly' ? 'year' : 'month'}
        </p>
      )}

      <form onSubmit={handleEmailSubmit} className="auth-form">
        <h2 className="auth-title">Continue with Email</h2>
        <p className="auth-subtitle">Enter your email address to continue</p>

        <div className="auth-input-container">
          <InputField
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            autoFocus={true}
            error={!!error}
          />
        </div>

        <InlineErrorMessage message={error} />

        <button
          type="submit"
          className={`auth-submit-button ${isValidEmail(email) && !isSending ? "enabled" : ""}`}
          disabled={!isValidEmail(email) || isSending}
        >
          {isSending ? 'Sending...' : 'Continue'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default EmailInputPage;
